import { useEffect, useMemo, useRef } from 'react';
// @ts-ignore
import { EditableToken, useContentToken } from '@vibesites/runtime/react';
import type { ComponentContentSchema } from '@vibesites/runtime';

const FLEX_WIDGET_ID = 'flex-widget';
const DEFAULT_FLEX_CLIENT_ID = 'recOwmE0Dr750mfxC';
const FLEX_WIDGET_VERSION = '6.9.4';
const FLEX_SCRIPT_BASE = 'https://sdk.getflex.com/widgets/v1/widgets.js';

type FlexWidgetRenderer = {
  render: (elementId: string) => void;
};

type FlexWidgetsApi = {
  Horizontal: () => FlexWidgetRenderer;
};

declare global {
  interface Window {
    FlexWidgets?: FlexWidgetsApi;
  }
}

function getFlexScripts() {
  return Array.from(document.querySelectorAll<HTMLScriptElement>('script')).filter(script =>
    script.src.startsWith(FLEX_SCRIPT_BASE)
  );
}

function getConfiguredFlexScript() {
  return getFlexScripts().find(script => script.src.includes('clientId=')) ?? null;
}

/**
 * Flex Payment Banner mounts the Flex SDK host and loads the SDK only after
 * the host element exists in the DOM. The Flex SDK exposes a `FlexWidgets`
 * global and requires an explicit `Horizontal().render('flex-widget')` call.
 */
export function FlexPaymentBanner() {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const configuredClientId = useContentToken('flexpaymentbanner.client_id');
  const clientId = configuredClientId?.trim() || DEFAULT_FLEX_CLIENT_ID;
  const scriptSrc = useMemo(() => {
    const params = new URLSearchParams({
      clientId,
      ver: FLEX_WIDGET_VERSION,
    });

    return `${FLEX_SCRIPT_BASE}?${params.toString()}`;
  }, [clientId]);

  const renderWidget = () => {
    const container = containerRef.current;
    const flexWidgets = window.FlexWidgets;

    if (!container || !flexWidgets) return;

    if (container.shadowRoot) return;

    flexWidgets.Horizontal().render(FLEX_WIDGET_ID);
  };

  useEffect(() => {
    const container = containerRef.current;
    const expectedScriptUrl = new URL(scriptSrc, window.location.href).href;

    if (!container || container.shadowRoot) return;

    const staleScripts = getFlexScripts().filter(script => script.src !== expectedScriptUrl);
    staleScripts.forEach(script => script.remove());

    const existingScript = getConfiguredFlexScript();

    const script = document.createElement('script');
    script.src = scriptSrc;
    script.async = true;
    script.onload = renderWidget;
    script.onerror = () => {
      console.error('Failed to load Flex widget SDK.');
    };

    if (window.FlexWidgets && existingScript) {
      renderWidget();
      return;
    }

    if (existingScript) {
      existingScript.addEventListener('load', renderWidget, { once: true });
      return;
    }

    document.body.appendChild(script);
  }, [scriptSrc]);

  return (
    <section style={{ padding: '12px 0 0' }}>
      <EditableToken
        path="flexpaymentbanner.client_id"
        type="text"
        label="Flex Client ID"
        as="span"
        style={{ display: 'none' }}
      >
        {clientId}
      </EditableToken>
      <div id={FLEX_WIDGET_ID} key={scriptSrc} ref={containerRef} />
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'FlexPaymentBanner',
  displayName: 'Flex Payment Banner',
  tokens: {
    'flexpaymentbanner.client_id': {
      label: 'Flex Client ID',
      type: 'text',
      defaultValue: 'recOwmE0Dr750mfxC',
      description: 'Client ID used to load the Flex payment banner SDK.',
    },
  },
};

(FlexPaymentBanner as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default FlexPaymentBanner;
